/**
 * Customers API service
 * Provides functions to interact with the customers API endpoints
 */

import { api } from './api';
import type { PaginatedResponse } from './api';

/**
 * Vehicle entity for customer details
 */
export interface CustomerVehicle {
  id: string;
  customerId: string;
  registration: string;
  make: string;
  model: string;
  year?: number;
  color?: string;
  vin?: string;
  insuranceCompany?: string;
  insurancePolicy?: string;
  createdAt: number;
  updatedAt: number;
}

/**
 * Customer entity from API
 */
export interface Customer {
  id: string;
  email: string;
  name: string;
  phone: string;
  countryCode?: string;
  dni?: string;
  address?: string;
  consentMarketing: boolean;
  consentDate?: number;
  createdAt: number;
  updatedAt: number;
  vehicles?: CustomerVehicle[];
}

/**
 * Customer search parameters
 */
export interface CustomerSearchParams {
  q?: string;
  limit?: number;
  offset?: number;
}

/**
 * Create customer request data
 */
export interface CreateCustomerData {
  email: string;
  name: string;
  phone: string;
  address?: string;
  consentMarketing?: boolean;
}

/**
 * Update customer request data
 */
export interface UpdateCustomerData {
  email?: string;
  name?: string;
  phone?: string;
  address?: string | null;
  consentMarketing?: boolean;
}

/**
 * Customer list response from API
 */
interface CustomerListResponse {
  items: Customer[];
  limit: number;
  offset: number;
}

/**
 * Search/list customers with optional query
 */
export async function list(params: CustomerSearchParams = {}): Promise<PaginatedResponse<Customer>> {
  const searchParams = new URLSearchParams();

  if (params.q) searchParams.set('q', params.q);
  if (params.limit) searchParams.set('limit', params.limit.toString());
  if (params.offset) searchParams.set('offset', params.offset.toString());

  const queryString = searchParams.toString();
  const endpoint = queryString ? `/api/customers?${queryString}` : '/api/customers';

  const response = await api.get<CustomerListResponse>(endpoint);

  return {
    items: response.items,
    total: response.items.length + (response.offset || 0),
    limit: response.limit,
    offset: response.offset,
  };
}

/**
 * Search customers by query (for typeahead)
 */
export async function search(query: string, limit = 10): Promise<Customer[]> {
  if (!query || query.length < 2) return [];

  const response = await api.get<CustomerListResponse>(
    `/api/customers?q=${encodeURIComponent(query)}&limit=${limit}`
  );

  return response.items;
}

/**
 * Get customer by ID with vehicles
 */
export async function getById(id: string): Promise<Customer> {
  return api.get<Customer>(`/api/customers/${id}`);
}

/**
 * Create a new customer
 */
export async function create(data: CreateCustomerData): Promise<Customer> {
  return api.post<Customer>('/api/customers', data);
}

/**
 * Update an existing customer
 */
export async function update(id: string, data: UpdateCustomerData): Promise<Customer> {
  return api.patch<Customer>(`/api/customers/${id}`, data);
}

/**
 * Delete a customer
 * Note: API may not support this yet
 */
export async function remove(id: string): Promise<void> {
  await api.delete(`/api/customers/${id}`);
}

/**
 * Get customer's vehicles
 */
export async function getVehicles(customerId: string): Promise<CustomerVehicle[]> {
  return api.get<CustomerVehicle[]>(`/api/customers/${customerId}/vehicles`);
}

/**
 * Customer activity item
 */
export interface CustomerActivity {
  type: 'booking' | 'service' | 'campaign_email' | 'booking_event';
  id: string;
  date: string;
  title: string;
  description: string;
  status?: string;
  metadata?: Record<string, unknown>;
  eventType?: string;
}

/**
 * Customer metrics summary
 */
export interface CustomerMetrics {
  totalVisits: number;
  totalSpend: number;
  lastVisitDate: string | null;
  firstVisitDate: string | null;
  averageSpend: number;
  vehicleCount: number;
  preferredServices: Array<{ serviceType: string; count: number }>;
  emailsReceived: number;
  emailsOpened: number;
}

/**
 * Customer activity summary response
 */
export interface CustomerActivitySummary {
  metrics: CustomerMetrics;
  recentActivity: CustomerActivity[];
}

/**
 * Get customer activity summary (metrics + recent activity)
 */
export async function getActivity(customerId: string, limit = 20): Promise<CustomerActivitySummary> {
  return api.get<CustomerActivitySummary>(`/api/customers/${customerId}/activity?limit=${limit}`);
}

/**
 * GDPR Data Export - export all customer data
 */
export interface GdprDataExport {
  customer: {
    id: string;
    email: string;
    name: string;
    phone: string;
    address: string | null;
    consentMarketing: boolean;
    consentDate: string | null;
    gdprConsentAt: string | null;
    gdprConsentVersion: string | null;
    createdAt: string;
    updatedAt: string;
  };
  vehicles: Array<{
    id: string;
    registration: string;
    make: string;
    model: string;
    year: number | null;
    color: string | null;
    vin: string | null;
    createdAt: string;
  }>;
  bookings: Array<{
    id: string;
    vehicleId: string;
    scheduledAt: string;
    workflowStage: string;
    type: string;
    notes: string | null;
    createdAt: string;
  }>;
  serviceHistory: Array<{
    id: string;
    vehicleId: string;
    serviceTypeId: string;
    performedAt: string;
    cost: number | null;
    notes: string | null;
    createdAt: string;
  }>;
  marketingEmails: Array<{
    id: string;
    campaignId: string;
    emailStatus: string;
    sentAt: string | null;
    openedAt: string | null;
    clickedAt: string | null;
  }>;
  exportedAt: string;
  gdprVersion: string;
}

/**
 * Export customer data for GDPR compliance
 */
export async function exportGdprData(customerId: string): Promise<GdprDataExport> {
  return api.get<GdprDataExport>(`/api/customers/${customerId}/gdpr/export`);
}

/**
 * Delete customer data for GDPR compliance (soft delete + anonymize)
 */
export async function gdprDelete(customerId: string): Promise<{ message: string }> {
  return api.post<{ message: string }>(`/api/customers/${customerId}/gdpr/delete`, {});
}

export const customersApi = {
  list,
  search,
  getById,
  create,
  update,
  delete: remove,
  getVehicles,
  getActivity,
  exportGdprData,
  gdprDelete,
};
